/**
 * The AI assistants authorized against this network, and how to take one back.
 *
 * One component, two audiences. `scope="mine"` is the personal list on account
 * settings; `scope="all"` is the administrative one, which the server fills per row
 * from the caller's reach over each grant's context. They differ only in which rows
 * arrive and whether the approver is worth naming, so they are not worth two
 * components that would drift.
 */
import { useMemo } from 'react';
import { Bot, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/hooks/useConfirm';
import { useAllMcpGrants, useMcpGrants, useRevokeMcpGrant, type McpGrant } from '@/hooks/useMcpGrants';

/** The two seeded presets, worded for a reader rather than a role table. */
function describePreset(preset: string): string {
  switch (preset) {
    case 'read_plan':
      return 'Read & plan';
    case 'read_plan_execute':
      return 'Read, plan & execute';
    default:
      // A preset added server-side before this client knows it: show the raw name
      // rather than inventing a friendly one that might understate what it allows.
      return preset;
  }
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

interface Props {
  scope?: 'mine' | 'all';
}

export function AuthorizedAgents({ scope = 'mine' }: Props) {
  const mine = useMcpGrants();
  const all = useAllMcpGrants(scope === 'all');
  const { data: grants, isLoading, isError } = scope === 'all' ? all : mine;
  const revoke = useRevokeMcpGrant();
  const { confirm, confirmDialog } = useConfirm();

  // Recently used first, then never-used, then by age. Someone auditing wants the live
  // ones at the top; someone tidying up wants the dormant ones findable, not buried
  // among rows that are all doing something.
  const ordered = useMemo(
    () =>
      [...(grants ?? [])].sort((a, b) => {
        if (!!a.lastUsedAt !== !!b.lastUsedAt) return a.lastUsedAt ? -1 : 1;
        if (a.lastUsedAt && b.lastUsedAt) return b.lastUsedAt.localeCompare(a.lastUsedAt);
        return b.createdAt.localeCompare(a.createdAt);
      }),
    [grants],
  );

  const revokeAgent = async (grant: McpGrant) => {
    const owner = grant.isOwn ? '' : ` approved by ${grant.createdByName ?? 'another user'}`;
    const ok = await confirm({
      title: 'Revoke this assistant?',
      message:
        `${grant.name}${owner} will stop working immediately — its next request is refused, ` +
        'not its next token. Reconnecting means approving it again from the assistant.',
      isDanger: true,
      confirmText: 'Revoke',
    });
    if (!ok) return;
    revoke.mutate(grant.id);
  };

  return (
    <section className="bg-card border border-border rounded-lg">
      <header className="px-5 py-4 border-b border-border">
        <h2 className="text-sm font-semibold">
          {scope === 'all' ? 'Authorized assistants' : 'Your authorized assistants'}
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          {scope === 'all'
            ? 'Every assistant connected to this organization that you can administer. Revoking one stops it on its next request.'
            : 'Assistants you have connected. Revoking one stops it on its next request.'}
        </p>
      </header>

      <div className="p-5">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : isError ? (
          <p className="text-xs text-muted-foreground">Couldn&rsquo;t load authorized assistants.</p>
        ) : ordered.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {scope === 'all'
              ? 'No assistants are connected to this organization.'
              : 'You haven’t connected an assistant yet.'}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {ordered.map((grant) => (
              <li key={grant.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <Bot className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm truncate">{grant.name}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {describePreset(grant.preset)}
                    </Badge>
                    {scope === 'all' && grant.isOwn && (
                      <Badge variant="secondary" className="text-[10px]">
                        Yours
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {grant.contextName ?? 'Unknown context'}
                    {grant.clientId ? ` · ${grant.clientId}` : ''}
                    {scope === 'all' && !grant.isOwn && grant.createdByName
                      ? ` · approved by ${grant.createdByName}`
                      : ''}
                    {' · '}
                    {grant.lastUsedAt ? `last used ${formatWhen(grant.lastUsedAt)}` : 'never used'}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { void revokeAgent(grant); }}
                  disabled={revoke.isPending}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {confirmDialog}
    </section>
  );
}
